import './antennaControls.scss'
import templateUrl from './antennaControls.html'

/**
 * Antenna Controls component.
 *
 * Start, stop, clear and submit buttons. Submit button is optional.
 * Read events are bubbled up: you will usually want to add a callback
 * in `on-item-read`.
 *
 * @example
 *   <tmr-antenna-controls
 *     antenna="$ctrl.antenna"
 *     disabled="$ctrl.antenna.$working"
 *     start-params="$ctrl.startParams"
 *     show-options="true"
 *     submit="SOME.LABEL"
 *     on-start="$ctrl.onStart()"
 *     on-stop="$ctrl.onStop()"
 *     on-epc-read="$ctrl.onEpcRead(epc)"
 *     on-epc-ignored="$ctrl.onEpcIgnored(epc)"
 *     on-item-read="$ctrl.onItemRead(item, epc)"
 *     on-item-clear="$ctrl.onItemClear(epc)"
 *     on-clear="$ctrl.antenna.$clearEpcs()"
 *   ></tmr-antenna-controls>
 *
 * @example <caption>With custom callback</caption>
 *   <tmr-antenna-controls
 *     antenna="$ctrl.antenna"
 *     message-callback="$ctrl.onMessage({ data })"
 *   ></tmr-antenna-controls>
 *
 *   // In controller
 *   onMessage({ data }) {
 *     // First call regular callback...
 *     this.antenna.$readEpc.call(this.antenna, { data });
 *     // Then do custom stuff with data...
 *   }
 *
 * @see EpcReaderInterface
 * @type {ngComponent}
 * @prop {Antenna} antenna
 * @prop {boolean} disabled If true, the controls are disabled
 * @prop {object} start-params Parameters passed to the Antenna when starting. For available params, @see {@link Antenna.$start}
 * @prop {boolean=} show-options If true, show a gear button with advanced options
 * @prop {function=} message-callback A function that is executed when a tag is read. Use with care, as the function must then take care to call {@link EpcReaderInterface.$readEpc} manually.
 * @prop {string=} submit The submit button translation label; if omitted, the submit button is not rendered
 * @prop {boolean=} submit-disabled When true, the button is disabled (note that it might be
 *   disabled by internal logic as well)
 * @prop {string|false=} [clear=] The clear button translation label; if false, the clear button is not rendered
 * @prop {function} on-start
 * @prop {function} on-stop
 * @prop {function} on-epc-read
 * @prop {function} on-epc-ignored Event fired when decoding fails (the epc has no item)
 * @prop {function} on-item-read
 * @prop {function} on-item-clear
 * @prop {function} on-clear
 * @prop {function=} on-submit
 */
export const tmrAntennaControls = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrAntennaControlsController',
  bindings: {
    antenna: '<',
    disabled: '<',
    startParams: '<',
    showOptions: '<',
    messageCallback: '&?',
    submit: '@?',
    submitDisabled: '<',
    clear: '<?',
    onStart: '&',
    onStop: '&',
    onEpcRead: '&',
    onEpcIgnored: '&',
    onItemRead: '&',
    onItemClear: '&',
    onClear: '&',
    onSubmit: '&',
  },
}

/**
 * Controller of component {@link tmrAntennaControls}.
 *
 * @type {ngController}
 */
export class tmrAntennaControlsController {
  static ngType = 'controller'

  constructor($rootScope, TmrLocalStorage, TmrToast) {
    'ngInject'
    Object.assign(this, { TmrLocalStorage, TmrToast })
    this.deregisterEpc = $rootScope.$on('epcread', (event, epc, antenna) =>
      this.onEpcReadEvent(epc, antenna)
    )
    this.deregisterEpcIgnored = $rootScope.$on(
      'epcignored',
      (event, epc, antenna) => this.onEpcIgnoredEvent(epc, antenna)
    )
    this.deregisterItem = $rootScope.$on(
      'itemread',
      (event, item, antenna, epc) => this.onItemReadEvent(item, antenna, epc)
    )
    this.deregisterItemClear = $rootScope.$on('itemclear', (event, antenna) =>
      this.onItemClearEvent(antenna)
    )
  }

  $onInit() {
    this.startParams = this.startParams ?? {}
    this.startParams.$mode =
      this.startParams.$mode ??
      this.TmrLocalStorage.get('epcReaderDefaultMode', 'persistent')
    this.clear = this.clear ?? 'ANTENNACONTROLS.CLEAR'
  }

  $onDestroy() {
    this.deregisterEpc()
    this.deregisterEpcIgnored()
    this.deregisterItem()
    this.deregisterItemClear()
  }

  start() {
    return this.antenna
      .$start(this.startParams, undefined, this.messageCallback)
      .then(
        () => this.onStart(),
        err => this.TmrToast.exception(err)
      )
  }

  stop() {
    return this.antenna.$stop().then(() => this.onStop())
  }

  onModeChange(mode) {
    this.startParams.$mode = mode
  }

  onEpcReadEvent(epc, antenna) {
    if (this.antenna === antenna) this.onEpcRead({ epc })
  }

  onEpcIgnoredEvent(epc, antenna) {
    if (this.antenna === antenna) this.onEpcIgnored({ epc })
  }

  onItemReadEvent(item, antenna, epc) {
    if (this.antenna === antenna) this.onItemRead({ item, epc })
  }

  onItemClearEvent(antenna) {
    if (this.antenna === antenna) this.onItemClear()
  }
}
